import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { reportNumberFormat } from '../../../utils/reportFormat';
import { OtherReportLoader, OtherReportTable } from './OtherReportShared';

const DEFAULT_ENTRY_COLUMNS = [
  { key: 'accounts_head', label: 'Account Head' },
  { key: 'description', label: 'Description' },
  { key: 'amount', label: 'Amount', align: 'right' },
];

const renderSideCell = (row, column) => {
  const value = row[column.key];
  if (column.align === 'right' && value !== null && value !== undefined && value !== '') {
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
      return reportNumberFormat(numeric);
    }
  }
  return value ?? '';
};

const TransactionSidePanel = ({ title, subtitle, side, columns }) => {
  const entryColumns = columns || DEFAULT_ENTRY_COLUMNS;
  const lines = side?.lines || [];

  return (
    <div className="trans-detail-side-panel">
      <h6 className="fw-semibold text-center mb-1">{title}</h6>
      <p className="text-muted text-center small mb-3">{subtitle}</p>
      {lines.length > 0 ? (
        <OtherReportTable columns={entryColumns}>
          <tbody>
            {lines.map((row, rowIndex) => (
              <tr key={`${title}-${rowIndex}`}>
                {entryColumns.map((column) => (
                  <td
                    key={`${title}-${rowIndex}-${column.key}`}
                    className={column.align === 'right' ? 'text-end' : ''}
                  >
                    {renderSideCell(row, column)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </OtherReportTable>
      ) : (
        <p className="text-muted text-center mb-0">No entries</p>
      )}
      <div className="text-end fw-bold mt-2">
        Total:
        {' '}
        {reportNumberFormat(side?.total ?? 0)}
      </div>
    </div>
  );
};

const AllTransactionsDetailModal = ({
  transId,
  detail,
  loading,
  error,
  onClose,
}) => {
  useEffect(() => {
    if (!transId) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.body.classList.add('modal-open');
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.classList.remove('modal-open');
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [transId, onClose]);

  if (!transId) {
    return null;
  }

  const hasDrCrLayout = detail?.debit_side || detail?.credit_side;
  const entryColumns = detail?.entry_columns || DEFAULT_ENTRY_COLUMNS;
  const fullColumns = detail?.columns || [];
  const fullLines = detail?.lines || [];
  const hasLines = (detail?.debit_side?.lines?.length || 0) + (detail?.credit_side?.lines?.length || 0) > 0
    || fullLines.length > 0;

  return ReactDOM.createPortal(
    <>
      <div className="modal-overlay" role="presentation" onClick={onClose} />
      <div className="modal-wrapper all-transactions-detail-wrapper">
        <div className="modal modal-dialog all-transactions-detail-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                Transaction
                {' '}
                <code>{transId}</code>
              </h5>
              <button type="button" className="btn-close modal-close-button" aria-label="Close" onClick={onClose}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              {detail?.pump_name ? (
                <p className="text-muted mb-2">{detail.pump_name}</p>
              ) : null}
              {detail?.ac_date ? (
                <p className="text-muted mb-3">
                  Date:
                  {' '}
                  {detail.ac_date}
                </p>
              ) : null}

              {loading && <OtherReportLoader message="Loading transaction lines" />}

              {!loading && error ? (
                <div className="alert alert-danger mb-0">{error}</div>
              ) : null}

              {!loading && !error && hasDrCrLayout && hasLines && (
                <div className="trans-detail-dr-cr mb-4">
                  <TransactionSidePanel
                    title={detail.debit_side?.label || 'Debit (Dr)'}
                    subtitle={detail.debit_side?.subtitle || 'Debtor'}
                    side={detail.debit_side}
                    columns={entryColumns}
                  />
                  <TransactionSidePanel
                    title={detail.credit_side?.label || 'Credit (Cr)'}
                    subtitle={detail.credit_side?.subtitle || 'Creditor'}
                    side={detail.credit_side}
                    columns={entryColumns}
                  />
                </div>
              )}

              {!loading && !error && fullLines.length > 0 && fullColumns.length > 0 && (
                <div className="all-transactions-full-lines">
                  <h6 className="fw-semibold mb-2">All ledger line fields</h6>
                  <OtherReportTable columns={fullColumns}>
                    <tbody>
                      {fullLines.map((row, rowIndex) => (
                        <tr key={`tx-full-line-${rowIndex}`}>
                          {fullColumns.map((column) => (
                            <td
                              key={`${rowIndex}-${column.key}`}
                              className={column.align === 'right' ? 'text-end' : ''}
                            >
                              {renderSideCell(row, column)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </OtherReportTable>
                </div>
              )}

              {!loading && !error && !hasDrCrLayout && fullLines.length > 0 && fullColumns.length === 0 && (
                <OtherReportTable columns={detail.columns || []}>
                  <tbody>
                    {fullLines.map((row, rowIndex) => (
                      <tr key={`tx-line-${rowIndex}`}>
                        {(detail.columns || []).map((column) => (
                          <td
                            key={`${rowIndex}-${column.key}`}
                            className={column.align === 'right' ? 'text-end' : ''}
                          >
                            {renderSideCell(row, column)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </OtherReportTable>
              )}

              {!loading && !error && detail && !hasLines && (
                <p className="text-muted mb-0">No ledger lines found for this transaction.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default AllTransactionsDetailModal;
